import * as React from 'react';
import styles from './index.less';
import FDrawer from '@/components/FDrawer';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FResourceTypeFilter from '@/components/FResourceTypeFilter';
import FComponentsLib from '@freelog/components-lib';
import FIcons from '@/components/FIcons';
import FOverflowTooltip from '@/components/FOverflowTooltip';
import FCoverImage from '@/components/FCoverImage';
import * as AHooks from 'ahooks';
import fMessage from '@/components/fMessage';
import AddItemsFromResourcesDrawer from '../AddItemsFromResourcesDrawer';
import FListFooter, { listStateAndListMore } from '@/components/FListFooter';
import FLoadingTip from '@/components/FLoadingTip';
import FNoDataTip from '@/components/FNoDataTip';
import FResourceStatusBadge from '@/components/FResourceStatusBadge';
import FInput_Search from '@/components/FInput_Search';

interface AddItemsFromExhibitsDrawerProps {
  open: boolean;
  nodeID: number;
  exhibitID: string;
  onClose?: () => void;
}

interface AddItemsFromExhibitsDrawerStates {
  selectedResourceType: {
    value: string;
    label: string;
    values: string[];
    labels: string[];
  };
  keywordsInputFilter: string;
  listTotal: number;
  listState: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
  listMore: 'loading' | 'andMore' | 'noMore';
  exhibitList: {
    added: boolean;
    id: string;
    cover: string;
    title: string;
    name: string;
    resourceType: string[];
    isOnline: boolean;
    authErrorText: string;
    resourceInfo: {
      id: string;
      name: string;
      title: string;
    };
    isFreeze: boolean;
  }[];
}

const defaultStates: AddItemsFromExhibitsDrawerStates = {
  selectedResourceType: {
    value: '#all',
    label:
      // '所有类型'
      FI18n.i18nNext.t('filter_resourcetype_option_all'),
    values: ['#all'],
    labels: [
      // '所有类型'
      FI18n.i18nNext.t('filter_resourcetype_option_all'),
    ],
  },
  keywordsInputFilter: '',
  listTotal: 0,
  listState: 'loading',
  listMore: 'loading',
  exhibitList: [],
};

function AddItemsFromExhibitsDrawer({
  open,
  nodeID,
  exhibitID,
  onClose,
}: AddItemsFromExhibitsDrawerProps) {
  const [$selectedResourceType, set$selectedResourceType, get$selectedResourceType] =
    FUtil.Hook.useGetState<AddItemsFromExhibitsDrawerStates['selectedResourceType']>(
      defaultStates.selectedResourceType,
    );
  const [$keywordsInputFilter, set$keywordsInputFilter, get$keywordsInputFilter] =
    FUtil.Hook.useGetState<AddItemsFromExhibitsDrawerStates['keywordsInputFilter']>(
      defaultStates.keywordsInputFilter,
    );
  const [$listTotal, set$listTotal, get$listTotal] = FUtil.Hook.useGetState<
    AddItemsFromExhibitsDrawerStates['listTotal']
  >(defaultStates.listTotal);
  const [$listState, set$listState, get$listState] = FUtil.Hook.useGetState<
    AddItemsFromExhibitsDrawerStates['listState']
  >(defaultStates.listState);
  const [$listMore, set$listMore, get$listMore] = FUtil.Hook.useGetState<
    AddItemsFromExhibitsDrawerStates['listMore']
  >(defaultStates.listMore);
  const [$exhibitList, set$exhibitList, get$exhibitList] = FUtil.Hook.useGetState<
    AddItemsFromExhibitsDrawerStates['exhibitList']
  >(defaultStates.exhibitList);

  const [$openResourceDrawer, set$openResourceDrawer, get$openResourceDrawer] =
    FUtil.Hook.useGetState<boolean>(false);

  const { run: run_FetchExhibitList } = AHooks.useDebounceFn(
    async () => {
      await fetchExhibitList(true);
    },
    {
      wait: 300,
      leading: true,
      trailing: true,
    },
  );

  async function fetchExhibitList(restart: boolean = true) {
    const {
      data: data_NodeInfo,
    }: {
      data: {
        nodeId: number;
        nodeTitle: string;
        nodeName: string;
      };
    } = await FServiceAPI.Node.details({
      nodeId: nodeID,
    });

    const oldList: AddItemsFromExhibitsDrawerStates['exhibitList'] = restart
      ? []
      : get$exhibitList();
    if (restart) {
      set$listState('loading');
    } else {
      set$listMore('loading');
    }

    const resourceTypes: string[] = get$selectedResourceType().value.split('#');
    let resourceTypeCode: string = resourceTypes[0];
    let resourceTypeCategory: 1 | 2 = 1;
    let resourceType: string = '';

    if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'other') {
      resourceTypeCode = resourceTypes[0];
      resourceTypeCategory = 2;
    }

    if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'all') {
      resourceType =
        get$selectedResourceType().labels[get$selectedResourceType().labels.length - 2];
    }

    const params: Parameters<typeof FServiceAPI.Exhibit.presentables>[0] = {
      nodeId: nodeID,
      limit: FUtil.Predefined.pageSize,
      skip: oldList.length,
      keywords: get$keywordsInputFilter() || undefined,
      // onlineStatus: Number(nodeManagerPage.exhibit_SelectedStatus),
      resourceType: resourceType || undefined,
      resourceTypeCode: resourceType === '' ? resourceTypeCode || undefined : undefined,
      omitResourceType: '主题',
      // isLoadPolicyInfo: 1,
      isLoadResourceDetailInfo: 1,
      // isLoadVersionProperty: 1,
      // isLoadVersionUpdateTip: 1,
      subjectType: 1,
      onlineStatus: 2,
    };
    // debugger;
    const {
      data: data_Exhibits,
    }: {
      data: {
        dataList: {
          presentableId: string;
          presentableIntro: string;
          presentableName: string;
          presentableTitle: string;
          coverImages: string[];
          version: string;
          onlineStatus: 0 | 1;
          resourceInfo: {
            resourceId: string;
            resourceName: string;
            resourceTitle: string;
            // subjectType: 1 | ;
            resourceType: string[];
            serializeStatus: 0 | 1;
            resourceVersions: {
              versionId: string;
            }[];
            latestVersion: string;
            status: 0 | 1 | 2 | 4;
          };
          policies: any[];
          subjectType: 1 | 4 | 5;
        }[];
        totalItem: number;
      };
    } = await FServiceAPI.Exhibit.presentables(params);

    let data_IsExist: {
      isExists: boolean;
      mountId: string;
    }[] = [];
    if (data_Exhibits.dataList.length > 0) {
      const {
        ret,
        errCode,
        msg,
        data,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          isExists: boolean;
          mountId: string;
        }[];
      } = await FServiceAPI.Exhibit.getItemsFromPresentableCollectionIsExist({
        presentableId: exhibitID,
        mountIds: data_Exhibits.dataList.map((i) => i.presentableId).join(','),
        idType: 'presentableId',
      });
      data_IsExist = data;
    }

    const exhibitList = [
      ...oldList,
      ...data_Exhibits.dataList.map<AddItemsFromExhibitsDrawerStates['exhibitList'][number]>(
        (i) => {
          const isAdded =
            data_IsExist.find((j) => j.mountId === i.presentableId)?.isExists ?? false;
          return {
            added: isAdded,
            id: i.presentableId,
            cover: i.coverImages[0],
            title: i.presentableTitle,
            name: data_NodeInfo.nodeName + '/' + i.presentableName,
            resourceType: i.resourceInfo.resourceType,
            isOnline: i.onlineStatus === 1,
            authErrorText: '',
            resourceInfo: {
              id: i.resourceInfo.resourceId,
              name: i.resourceInfo.resourceName,
              title: i.resourceInfo.resourceTitle,
            },
            isFreeze: i.resourceInfo.status === 2,
          };
        },
      ),
    ];

    const { state, more } = listStateAndListMore({
      list_Length: exhibitList.length,
      total_Length: data_Exhibits.totalItem,
      has_FilterCriteria:
        get$keywordsInputFilter() !== '' || get$selectedResourceType().value !== '#all',
    });

    set$listState(state);
    set$listMore(more);
    set$exhibitList(exhibitList);
  }

  return (
    <FDrawer
      open={open}
      title={
        // '节点展品'
        FI18n.i18nNext.t('additem_exhibit_title')
      }
      width={700}
      onClose={() => {
        onClose?.();
      }}
      afterOpenChange={(open) => {
        if (open) {
          fetchExhibitList(true);
        } else {
          set$listState(defaultStates.listState);
          set$listMore(defaultStates.listMore);
          set$exhibitList(defaultStates.exhibitList);
          set$listTotal(defaultStates.listTotal);
          set$keywordsInputFilter(defaultStates.keywordsInputFilter);
          set$selectedResourceType(defaultStates.selectedResourceType);
        }
      }}
    >
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 30,
            }}
          >
            <FResourceTypeFilter
              subjectType={'resource'}
              value={$selectedResourceType}
              omitTheme={true}
              onChange={(value) => {
                set$selectedResourceType(value);
                run_FetchExhibitList();
              }}
            />

            <FComponentsLib.FTextBtn
              onClick={() => {
                set$openResourceDrawer(true);
              }}
            >
              {
                // 添加展品
                FI18n.i18nNext.t('additem_exhibit_addmoreexhibit')
              }
            </FComponentsLib.FTextBtn>
          </div>

          {/* <FComponentsLib.FInput.FSearch */}
          <FInput_Search
            style={{
              width: 200,
            }}
            value={$keywordsInputFilter}
            onChange={(e) => {
              set$keywordsInputFilter(e.target.value);
              run_FetchExhibitList();
            }}
            className={styles.input}
            placeholder={
              // 搜索展品
              FI18n.i18nNext.t('additem_exhibit_exhlist_search_hint')
            }
          />
        </div>

        <div style={{ height: 30 }} />

        {$listState === 'loading' && <FLoadingTip height={600} />}

        {$listState === 'noData' && <FNoDataTip height={600} tipText={'暂无数据'} />}
        {$listState === 'noSearchResult' && <FNoDataTip height={600} tipText={'无搜索结果'} />}

        {$listState === 'loaded' && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 5,
                width: '100%',
              }}
            >
              {$exhibitList.map((i) => {
                return (
                  <ExhibitItem
                    key={i.id}
                    item={i}
                    onAdd={async () => {
                      const {
                        ret,
                        errCode,
                        msg,
                        data,
                      }: {
                        ret: number;
                        errCode: number;
                        msg: string;
                        data: {
                          addFailedItems: {
                            presentableId: string;
                            reason: string;
                          }[];
                        };
                      } = await FServiceAPI.Exhibit.addItemsToPresentableCollection({
                        presentableId: exhibitID,
                        addCollectionItems: [{ presentableId: i.id }],
                      });
                      if (ret !== 0 || errCode !== 0) {
                        fMessage(msg, 'error');
                        return;
                      }

                      if (data.addFailedItems.length > 0) {
                        fMessage(data.addFailedItems.map((j) => j.reason).join('\n'), 'error');
                        return;
                      }

                      set$exhibitList(
                        get$exhibitList().map((j) => {
                          if (j.id === i.id) {
                            return { ...j, added: true };
                          }
                          return j;
                        }),
                      );
                    }}
                    onDelete={async () => {
                      set$exhibitList(
                        get$exhibitList().map((j) => {
                          if (j.id === i.id) {
                            return { ...j, added: false };
                          }
                          return j;
                        }),
                      );
                      const { ret, errCode, msg } =
                        await FServiceAPI.Exhibit.removeItemsFromPresentableCollection({
                          presentableId: exhibitID,
                          removeIds: i.id,
                          idType: 'presentableId',
                        });
                      if (ret !== 0 || errCode !== 0) {
                        fMessage(msg, 'error');
                      }
                    }}
                  />
                );
              })}
            </div>

            <FListFooter
              state={$listMore}
              onClickLoadMore={() => {
                fetchExhibitList(false);
              }}
            />
          </>
        )}
      </div>
      <AddItemsFromResourcesDrawer
        open={$openResourceDrawer}
        nodeID={nodeID}
        exhibitID={exhibitID}
        onClose={() => {
          set$openResourceDrawer(false);
          fetchExhibitList(true);
        }}
      />
    </FDrawer>
  );
}

export default AddItemsFromExhibitsDrawer;

interface ExhibitItemProps {
  item: AddItemsFromExhibitsDrawerStates['exhibitList'][number];
  onAdd?: () => void;
  onDelete?: () => void;
}

function ExhibitItem({ item, onAdd, onDelete }: ExhibitItemProps) {
  const [$showDeleteButton, set$showDeleteButton, get$showDeleteButton] =
    FUtil.Hook.useGetState<boolean>(false);

  return (
    <div
      className={styles.itemCard}
      onMouseEnter={() => {
        console.log('onMouseEnter', item.added);
        if (item.added) {
          set$showDeleteButton(true);
        }
      }}
      onMouseLeave={() => {
        set$showDeleteButton(false);
      }}
    >
      <div
        style={{ opacity: item.isFreeze ? 0.4 : 1 }}
        className={styles.itemCard_item}
        onClick={() => {
          if (!item.added) {
            onAdd?.();
          }
        }}
      >
        <div className={styles.itemCard_item_content}>
          <div
            style={{
              width: 'fit-content',
              height: 'fit-content',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            <FCoverImage src={item.cover} width={104} style={{ flexShrink: 0 }} />
            {(item.isFreeze || !item.isOnline) && (
              <FResourceStatusBadge
                status={item.isFreeze ? 'freeze' : 'offline'}
                style={{ position: 'absolute', top: 10, left: 10 }}
              />
            )}
          </div>
          <div style={{ flexShrink: 1, width: '100%' }}>
            <FOverflowTooltip
              text={item.title}
              style={{
                maxWidth: 380,
                color: '#222',
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: '600',
              }}
            />
            <div style={{ height: 7 }} />
            <div className={styles.itemCard_item_content_info}>
              <FOverflowTooltip
                text={`${item.resourceType.join('/')} | 授权标识：${item.name}`}
                style={{
                  maxWidth: 340,
                  flexShrink: 1,
                  lineHeight: '18px',
                  color: '#999',
                  fontSize: '12px',
                }}
              />
            </div>
            <div style={{ height: 7 }} />
            <div className={styles.itemCard_item_content_policies}>
              <FComponentsLib.FContentText text={'更多信息：'} type={'additional2'} />
              <FComponentsLib.FTextBtn
                style={{
                  textDecoration: 'underline',
                  fontSize: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 260,
                  // width: '100%',
                  display: 'inline-block',
                }}
                onClick={() => {
                  self.open(FUtil.LinkTo.resourceDetails({ resourceID: item.resourceInfo.id }));
                }}
                type={'default'}
              >
                {item.resourceInfo.name}
              </FComponentsLib.FTextBtn>
            </div>
          </div>
        </div>

        {item.added && (
          <label className={styles.itemCard_item_selectedLabel}>
            <FIcons.FCheck style={{ fontSize: 14, color: '#2784FF' }} />

            <span
              style={{
                maxWidth: 55,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              {/* 已添加 */}
              {FI18n.i18nNext.t('additem_exhibit_label_added')}
            </span>
          </label>
        )}

        {item.isFreeze && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              cursor: 'not-allowed',
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          />
        )}
      </div>

      {$showDeleteButton && (
        <div
          className={styles.itemCard_deleteButton}
          onClick={() => {
            // onDelete?.({ id: i.resourceId, name: i.resourceName });
            onDelete?.();
          }}
        >
          {/* 移除 */}
          {FI18n.i18nNext.t('additem_exhibit_btn_remove')}
        </div>
      )}
    </div>
  );
}
